import {
  campaignHotelBadgeText,
  getDestinationCampaignHotels,
  resolveCampaignContract,
} from "../data/destinationCampaignHotels";
import { withBelvederePublicAsset } from "../data/belvedereRoomImages";
import { belvedereContract } from "../data/belvedereContractData";
import { getCompatibleRoomOffers } from "../services/belvederePricing";
import { getCompatibleRoyalTulipRoomOffers } from "../services/royalTulipPricing";
import { getDestinationResolveHints } from "./resolveAttractionsDestinationId";
import { isoDateFromDdMmYyyy } from "./agentRecentSearchPayload";
import { formatAgentHotelSearchApiPriceDisplay } from "./agentHotelSearchApiPrice";
import { normalizeDestinationCode } from "./destinationCampaignBadgeGate";

function collectHaystack({ searchPhrase, location }) {
  const parts = [];
  const push = (value) => {
    const s = String(value || "").trim();
    if (s) parts.push(s);
  };
  push(searchPhrase?.CITYNAME);
  push(searchPhrase?.CITYNAME_HEB);
  push(searchPhrase?.city);
  push(searchPhrase?.country);
  push(searchPhrase?.userQuery);
  push(location?.CITYNAME);
  push(location?.CITYNAME_HEB);
  push(location?.city);
  push(location?.name);
  push(location?.label);
  push(location?.fullname);
  return parts.join(" ").toLowerCase();
}

export function resolveHotelSearchDestinationCodes({ searchPhrase, location } = {}) {
  const hints = getDestinationResolveHints(location, searchPhrase);
  const codes = new Set();
  const fromHint = normalizeDestinationCode(hints?.lds_code);
  if (fromHint && /^[A-Z]{3}$/.test(fromHint)) codes.add(fromHint);
  for (const raw of [
    searchPhrase?.AIRPORTCODE,
    searchPhrase?.CITYCODE,
    location?.AIRPORTCODE,
    location?.CITYCODE,
  ]) {
    const code = normalizeDestinationCode(raw);
    if (code && /^[A-Z]{3}$/.test(code) && code !== "ALL") codes.add(code);
  }
  return [...codes];
}

export function matchDestinationCampaignHotels({ searchPhrase, location } = {}) {
  const codes = new Set(resolveHotelSearchDestinationCodes({ searchPhrase, location }));
  const haystack = collectHaystack({ searchPhrase, location });
  return getDestinationCampaignHotels().filter((campaign) => {
    if (campaign.destinationCodes.some((code) => codes.has(normalizeDestinationCode(code)))) {
      return true;
    }
    return (campaign.destinationNameHints || []).some((hint) =>
      haystack.includes(String(hint).toLowerCase()),
    );
  });
}

export function roomsFromHotelSearchPhrase(searchPhrase) {
  const list = Array.isArray(searchPhrase?.rooms) ? searchPhrase.rooms : [];
  if (list.length === 0) return [{ adults: 2, children: 0, childAges: [] }];
  return list.map((room) => {
    const ages = Array.isArray(room?.KidsAges)
      ? room.KidsAges.map((age) => Number(age)).filter((age) => Number.isFinite(age))
      : Array.isArray(room?.childAges)
        ? room.childAges.map((age) => Number(age)).filter((age) => Number.isFinite(age))
        : [];
    const adults = Math.max(1, Math.floor(Number(room?.AdultsCount ?? room?.adults) || 2));
    const children = ages.length || Math.max(0, Math.floor(Number(room?.children) || 0));
    return {
      adults,
      children,
      childAges: ages.slice(0, children),
    };
  });
}

function quoteCampaignStay(campaign, searchPhrase, { agentChannel, publicChannel } = {}) {
  const arrive =
    isoDateFromDdMmYyyy(searchPhrase?.checkIn) || isoDateFromDdMmYyyy(searchPhrase?.depDate);
  const depart =
    isoDateFromDdMmYyyy(searchPhrase?.checkOut) || isoDateFromDdMmYyyy(searchPhrase?.retDate);
  if (!arrive || !depart) return null;
  const rooms = roomsFromHotelSearchPhrase(searchPhrase);
  const pricingOptions = { agentChannel: Boolean(agentChannel), publicChannel: Boolean(publicChannel) };
  try {
    const contract = resolveCampaignContract(campaign);
    const offers =
      campaign.quoteKind === "royalTulip"
        ? getCompatibleRoyalTulipRoomOffers(
            rooms,
            arrive,
            depart,
            contract,
            pricingOptions,
          )
        : getCompatibleRoomOffers(
            rooms,
            arrive,
            depart,
            contract || belvedereContract,
            pricingOptions,
          );
    if (!Array.isArray(offers) || offers.length === 0) return null;
    const cheapest = offers.reduce((best, offer) =>
      Number(offer?.sellingTotal) < Number(best?.sellingTotal) ? offer : best,
    );
    const amount = Number(cheapest?.sellingTotal);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return { amount, arrive, depart, rooms };
  } catch {
    return null;
  }
}

function isDuplicateOfCampaign(hotel, campaign) {
  if (!hotel || hotel.isCampaignHotel) return false;
  const hotelId = hotel.ID ?? hotel.HotelId ?? hotel.hotelId ?? hotel.HotelID;
  if (
    campaign.gimmonixHotelId != null &&
    hotelId != null &&
    String(hotelId) === String(campaign.gimmonixHotelId)
  ) {
    return true;
  }
  const name = String(hotel.DisplayName || hotel.HotelName || hotel.Name || "");
  return campaign.duplicateNamePattern?.test(name) === true;
}

function campaignMeta(campaign, searchPhrase, quote) {
  const arrive =
    quote?.arrive ||
    isoDateFromDdMmYyyy(searchPhrase?.checkIn) ||
    isoDateFromDdMmYyyy(searchPhrase?.depDate);
  const depart =
    quote?.depart ||
    isoDateFromDdMmYyyy(searchPhrase?.checkOut) ||
    isoDateFromDdMmYyyy(searchPhrase?.retDate);
  return {
    isPinnedByHotelsRiver: true,
    isPinned: true,
    isCampaignHotel: true,
    pinnedBadgeText: campaignHotelBadgeText(campaign),
    pinnedCampaignDestinationCode: campaign.destinationCodes[0] || null,
    campaignSlug: campaign.slug,
    campaignQuoteKind: campaign.quoteKind,
    campaignHotelCode: campaign.hotelCode,
    campaignCurrency: campaign.currency,
    Currency: campaign.currency,
    campaignKosher: Boolean(campaign.kosher),
    gimmonixHotelId: campaign.gimmonixHotelId || null,
    skipInventoryHold: Boolean(campaign.skipInventoryHold),
    campaignArriveIso: arrive,
    campaignDepartIso: depart,
    hotelCity: campaign.destinationCodes[0] || null,
  };
}

export function attachCampaignToGimmonixHotel(hotel, campaign, searchPhrase, pricingChannel = {}) {
  const quote = quoteCampaignStay(campaign, searchPhrase, pricingChannel);
  const amount = quote?.amount;
  return {
    ...hotel,
    ...campaignMeta(campaign, searchPhrase, quote),
    LowestPackagePrice: amount ?? hotel?.LowestPackagePrice ?? null,
    HighestPackagePrice: amount ?? hotel?.HighestPackagePrice ?? null,
    _filterPrice: amount ?? hotel?._filterPrice ?? hotel?.LowestPackagePrice ?? null,
  };
}

export function buildCampaignHotelSearchRow(campaign, searchPhrase, pricingChannel = {}) {
  const quote = quoteCampaignStay(campaign, searchPhrase, pricingChannel);
  const image = withBelvederePublicAsset(campaign.image) || campaign.image;
  return {
    ID: campaign.id,
    DisplayName: campaign.displayName,
    Address: campaign.address,
    StarRating: campaign.starRating,
    DefaultImage: { FullSize: image },
    LowestPackagePrice: quote?.amount ?? null,
    HighestPackagePrice: quote?.amount ?? null,
    _filterPrice: quote?.amount ?? null,
    _stars: Number(campaign.starRating) || 5,
    hotelCity: campaign.destinationCodes[0] || null,
    checkIn: searchPhrase?.checkIn || null,
    checkOut: searchPhrase?.checkOut || null,
    ...campaignMeta(campaign, searchPhrase, quote),
  };
}

export function injectDestinationCampaignHotels(
  hotels,
  { searchPhrase, location, publicChannel = false } = {},
) {
  const list = Array.isArray(hotels) ? hotels : [];
  const campaigns = matchDestinationCampaignHotels({ searchPhrase, location });
  if (campaigns.length === 0) return list;

  const pricingChannel = {
    agentChannel: !publicChannel,
    publicChannel,
  };
  const usedIds = new Set();
  const pinned = [];
  const rest = [];

  for (const hotel of list) {
    if (hotel?.isCampaignHotel) {
      rest.push(hotel);
      continue;
    }
    const campaign = campaigns.find((row) => isDuplicateOfCampaign(hotel, row));
    if (campaign && !usedIds.has(campaign.id)) {
      pinned.push(attachCampaignToGimmonixHotel(hotel, campaign, searchPhrase, pricingChannel));
      usedIds.add(campaign.id);
      continue;
    }
    rest.push(hotel);
  }

  const missing = campaigns
    .filter((campaign) => !usedIds.has(campaign.id))
    .map((campaign) => buildCampaignHotelSearchRow(campaign, searchPhrase, pricingChannel));

  return [...pinned, ...missing, ...rest];
}

export function buildCampaignHotelLandingUrl(item, searchPhrase, { agentId } = {}) {
  const path = String(item?.campaignLandingPath || "").trim();
  if (!path) return null;
  const params = new URLSearchParams();
  params.set("source", "agent_dashboard");
  if (agentId != null && String(agentId).trim()) {
    params.set("agentId", String(agentId));
  }
  const arrive =
    item?.campaignArriveIso ||
    isoDateFromDdMmYyyy(searchPhrase?.checkIn) ||
    isoDateFromDdMmYyyy(searchPhrase?.depDate);
  const depart =
    item?.campaignDepartIso ||
    isoDateFromDdMmYyyy(searchPhrase?.checkOut) ||
    isoDateFromDdMmYyyy(searchPhrase?.retDate);
  if (arrive) params.set("arrive", arrive);
  if (depart) params.set("depart", depart);
  const rooms = roomsFromHotelSearchPhrase(searchPhrase);
  if (rooms.length > 0) params.set("rooms", JSON.stringify(rooms));
  return `${path}?${params.toString()}`;
}

export function convertCampaignContractAmountToDisplay({
  amount,
  contractCurrency,
  displayCurrency,
  currencyRate,
} = {}) {
  const raw = Number(amount);
  const from = String(contractCurrency || "USD").toUpperCase();
  const to = String(displayCurrency || from).toUpperCase();
  if (!Number.isFinite(raw) || raw <= 0) {
    return { amount: null, currency: to || from };
  }
  if (!to || from === to) {
    return { amount: raw, currency: from };
  }
  if (to === "ILS") {
    const rateKey = from === "EUR" ? "EUR" : from === "GBP" ? "GBP" : "USD";
    const rate = Number(currencyRate?.[rateKey] ?? currencyRate?.[from]);
    if (Number.isFinite(rate) && rate > 0) {
      return { amount: Math.round(raw * rate), currency: "ILS" };
    }
  }
  return { amount: raw, currency: from };
}

export function resolveCampaignHotelSearchDisplayPrice(
  item,
  { displayCurrency, currencyRate } = {},
) {
  return convertCampaignContractAmountToDisplay({
    amount: item?.LowestPackagePrice,
    contractCurrency: item?.campaignCurrency || "USD",
    displayCurrency,
    currencyRate,
  });
}

export function formatCampaignHotelSearchPrice(item, { displayCurrency, currencyRate } = {}) {
  const resolved = resolveCampaignHotelSearchDisplayPrice(item, {
    displayCurrency,
    currencyRate,
  });
  if (resolved.amount == null) return null;
  return formatAgentHotelSearchApiPriceDisplay(resolved.amount, resolved.currency);
}
